import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/var/www/html/src/components/Layout/ApiRef.js";
import CodeSnippet from 'components/CodeSnippet';
import * as CODE from '../../constants/voice-messaging';
import { APIWrapper, LeftColumn, RightColumn } from 'components/ApiRef';
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const Link = makeShortcode("Link");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h1>{`Voice Messaging API`}</h1>
    <p>{`MessageBird's Voice Messaging API enables you to transform text messages into voice messages to any country. Each voice message is identified by a unique random ID that can be used to check the status of the voice message. You can select from 26 languages and different attributes such as male or female voice, speaking rate, repeat, schedule, and more.`}</p>
    <p>{`The Voice Messaging API uses `}<inlineCode parentName="p">{`HTTP verbs`}</inlineCode>{` and a `}<inlineCode parentName="p">{`RESTful`}</inlineCode>{` endpoint structure with an access key that is used as the API Authorization framework. Request and response payloads are formatted as JSON—although we provide a GET alternative for requests—using UTF-8 encoding and URL encoded values.`}</p>
    <hr></hr>
    <APIWrapper mdxType="APIWrapper">
      <LeftColumn mdxType="LeftColumn">
        <h2>{`API Endpoint`}</h2>
        <p>{`All URLs referenced in MessageBird's Voice Messaging API documentation have the base URL in the code snippet on the right side:`}</p>
      </LeftColumn>
      <RightColumn mdxType="RightColumn">
  <h4>API Endpoint</h4>
  <CodeSnippet code={CODE.TTS_URI} mdxType="CodeSnippet" />
      </RightColumn>
    </APIWrapper>
    <hr></hr>
    <APIWrapper mdxType="APIWrapper">
      <LeftColumn mdxType="LeftColumn">
        <h2>{`Available HTTP methods`}</h2>
        <p>{`The MessageBird API uses HTTP verbs to understand if you want to read (`}<inlineCode parentName="p">{`GET`}</inlineCode>{`), delete (`}<inlineCode parentName="p">{`DELETE`}</inlineCode>{`) or create (`}<inlineCode parentName="p">{`POST`}</inlineCode>{`) an object. When your web application cannot do a `}<inlineCode parentName="p">{`POST`}</inlineCode>{` or `}<inlineCode parentName="p">{`DELETE`}</inlineCode>{`, we provide the ability to set the method through the query parameter `}<inlineCode parentName="p">{`_method`}</inlineCode>{`.`}</p>
      </LeftColumn>
      <RightColumn mdxType="RightColumn">
  <h4>Available HTTP methods</h4>
  <CodeSnippet code={CODE.TTS_METHODS} mdxType="CodeSnippet" />
      </RightColumn>
    </APIWrapper>
    <hr></hr>
    <APIWrapper mdxType="APIWrapper">
      <LeftColumn mdxType="LeftColumn">
        <h2>{`The voice message object`}</h2>
        <p>{`This object represents a voice message at MessageBird.com`}</p>
        <h4>{`Attributes`}</h4>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Attribute`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Type`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Description`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`id`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`string`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`A unique random ID which is created on the MessageBird platform and is returned upon creation of the object.`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`href`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`string`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`The URL of the created object.`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`reference`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`string`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`A client reference`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`originator`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`string`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`The sender of the message. A telephone number (including country code).`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`body`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`string`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`The body of the SMS message. The maxlength is 1000 characters. Check the `}<a parentName="td" {...{
                  "href": "/api/voice-messaging#the-voice-body"
                }}>{`voice body`}</a>{` for more options`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`language`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`string`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`The language in which the message needs to be read to the recipient. Possible values are: `}<inlineCode parentName="td">{`cy-gb`}</inlineCode>{`,`}<inlineCode parentName="td">{`da-dk`}</inlineCode>{`,`}<inlineCode parentName="td">{`de-de`}</inlineCode>{`,`}<inlineCode parentName="td">{`el-gr`}</inlineCode>{`,`}<inlineCode parentName="td">{`en-au`}</inlineCode>{`,`}<inlineCode parentName="td">{`en-gb`}</inlineCode>{`,`}<inlineCode parentName="td">{`en-gb-wls`}</inlineCode>{`,`}<inlineCode parentName="td">{`en-in`}</inlineCode>{`,`}<inlineCode parentName="td">{`en-us`}</inlineCode>{`,`}<inlineCode parentName="td">{`es-es`}</inlineCode>{`,`}<inlineCode parentName="td">{`es-mx`}</inlineCode>{`,`}<inlineCode parentName="td">{`es-us`}</inlineCode>{`,`}<inlineCode parentName="td">{`fr-ca`}</inlineCode>{`,`}<inlineCode parentName="td">{`fr-fr`}</inlineCode>{`,`}<inlineCode parentName="td">{`id-id`}</inlineCode>{`,`}<inlineCode parentName="td">{`is-is`}</inlineCode>{`,`}<inlineCode parentName="td">{`it-it`}</inlineCode>{`,`}<inlineCode parentName="td">{`ja-jp`}</inlineCode>{`,`}<inlineCode parentName="td">{`ko-kr`}</inlineCode>{`,`}<inlineCode parentName="td">{`ms-my`}</inlineCode>{`,`}<inlineCode parentName="td">{`nb-no`}</inlineCode>{`,`}<inlineCode parentName="td">{`nl-nl`}</inlineCode>{`,`}<inlineCode parentName="td">{`pl-pl`}</inlineCode>{`,`}<inlineCode parentName="td">{`pt-br`}</inlineCode>{`,`}<inlineCode parentName="td">{`pt-pt`}</inlineCode>{`,`}<inlineCode parentName="td">{`ro-ro`}</inlineCode>{`,`}<inlineCode parentName="td">{`ru-ru`}</inlineCode>{`,`}<inlineCode parentName="td">{`sv-se`}</inlineCode>{`,`}<inlineCode parentName="td">{`ta-in`}</inlineCode>{`,`}<inlineCode parentName="td">{`th-th`}</inlineCode>{`,`}<inlineCode parentName="td">{`tr-tr`}</inlineCode>{`,`}<inlineCode parentName="td">{`vi-vn`}</inlineCode>{`,`}<inlineCode parentName="td">{`zh-cn`}</inlineCode>{`,`}<inlineCode parentName="td">{`zh-hk`}</inlineCode>{`.`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`voice`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`string`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`The voice in which the messages needs to be read to the recipient. Possible values are: `}<inlineCode parentName="td">{`male`}</inlineCode>{`, `}<inlineCode parentName="td">{`female`}</inlineCode>{`.`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`repeat`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`int`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`The number of times the message needs to be repeated. Maximum is 10 times.`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ifMachine`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`string`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`What to do when a machine picks up the phone? Possible values are:`}<br />{`- `}<inlineCode parentName="td">{`continue`}</inlineCode>{` do not check, just play the message`}<br />{`- `}<inlineCode parentName="td">{`delay`}</inlineCode>{` if a machine answers, wait until the machine stops talking`}<br />{`- `}<inlineCode parentName="td">{`hangup`}</inlineCode>{` hangup when a machine answers`}<br /><em parentName="td">{`Default is: `}<inlineCode parentName="em">{`delay`}</inlineCode></em>{`.`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`machineTimeout`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`integer`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`The time (in milliseconds) to analyze if a machine has picked up the phone. Used in combination with the delay and hangup values of the ifMachine attribute. Minimum: 400, maximum: 10000. `}<em parentName="td">{`Default is: 7000`}</em>{`.`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`scheduledDatetime`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`datetime`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`The scheduled date and time of the message in RFC3339 format (Y-m-d\\TH:i:sP)`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`createdDatetime`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`datetime`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`The date and time of the creation of the message in RFC3339 format (Y-m-d\\TH:i:sP)`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`recipients`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`hash`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`The hash with recipient information. Further explanation in the table below.`}</td>
            </tr>
          </tbody>
        </table>
      </LeftColumn>
      <RightColumn mdxType="RightColumn">
  <h4>Object example</h4>
  <CodeSnippet code={CODE.TTS_OBJECT} mdxType="CodeSnippet" />
      </RightColumn>
    </APIWrapper>
    <h4>{`The recipients array`}</h4>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Attribute`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`totalCount`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`integer`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The total count of recipients.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`totalSentCount`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`integer`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The count of recipients that have the message pending (status `}<inlineCode parentName="td">{`calling`}</inlineCode>{`).`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`totalDeliveredCount`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`integer`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The count of recipients where the message is delivered (status `}<inlineCode parentName="td">{`answered`}</inlineCode>{`).`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`totalDeliveryFailedCount`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`integer`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The count of recipients where the delivery has failed (status `}<inlineCode parentName="td">{`failed`}</inlineCode>{`, `}<inlineCode parentName="td">{`busy`}</inlineCode>{`, `}<inlineCode parentName="td">{`machine`}</inlineCode>{`).`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`items`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`array`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An array of recipient hashes`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`items[].recipient`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`integer`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The msisdn of the recipient`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`items[].status`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The status of the message sent to the recipient. Possible values: `}<inlineCode parentName="td">{`calling`}</inlineCode>{`, `}<inlineCode parentName="td">{`answered`}</inlineCode>{`, `}<inlineCode parentName="td">{`failed`}</inlineCode>{`. For future use the following status are reserved: `}<inlineCode parentName="td">{`busy`}</inlineCode>{` and `}<inlineCode parentName="td">{`machine`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`items[].statusDatetime`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`datetime`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The datum time of the last status in RFC3339 format (Y-m-d\\TH:i:sP)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`items[].recipientCountry`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The name of the recipient’s original country, based on MSISDN.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`items[].recipientCountryPrefix`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`integer`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The prefix code for the recipient’s original country, based on MSISDN.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`items[].recipientOperator`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The name of the operator of the recipient. Identified by MCCMNC of the message.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`items[].messageLength`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`integer`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The length of the message in characters. Depends on the message datacoding.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`items[].statusReason`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The details about the message status. Possible values: `}<inlineCode parentName="td">{`successfully delivered`}</inlineCode>{`, `}<inlineCode parentName="td">{`pending DLR`}</inlineCode>{`, `}<inlineCode parentName="td">{`DLR not received`}</inlineCode>{`, `}<inlineCode parentName="td">{`incorrect number`}</inlineCode>{`, `}<inlineCode parentName="td">{`unknown subscriber`}</inlineCode>{`, `}<inlineCode parentName="td">{`expired`}</inlineCode>{`, `}<inlineCode parentName="td">{`timeout`}</inlineCode>{`, and `}<inlineCode parentName="td">{`generic delivery failure`}</inlineCode>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`items[].price`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`hashmap`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A hashmap with extra information about how much the message costs.`}<br /><em parentName="td">{`Note: see `}<a href="https://support.messagebird.com/hc/en-us/articles/360006537458-How-does-SMS-and-Voice-pricing-work" target="_blank" rel="noopener noreferrer">{`this article`}</a>{` for more details about our pricing policy.`}</em></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`items[].price.amount`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`float `}{`|`}{` null`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The price the message was billed with. It could have a null value if the message isn’t billed yet.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`items[].price.currency`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string `}{`|`}{` null`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The alphabetic code of the price currency, in ISO 4217 format. It could have null value if the message isn’t billed yet.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`items[].mccmnc`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string `}{`|`}{` null`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The code of the operator of the message sender. It could have null value if the message isn’t delivered yet.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`items[].mcc`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string `}{`|`}{` null`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The MCC (Mobile Country Code) part of MCCMNC.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`items[].mnc`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string `}{`|`}{` null`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The MNC (Mobile Network Code) part of MCCMNC.`}</td>
        </tr>
      </tbody>
    </table>
    <hr></hr>
    <APIWrapper mdxType="APIWrapper">
      <LeftColumn mdxType="LeftColumn">
        <h2>{`The voice body`}</h2>
        <p>{`By tweaking the body you can alter the speed and delays in the spoken text.`}</p>
        <p>{`You can add a small `}<inlineCode parentName="p">{`delay`}</inlineCode>{` between words or digits by adding a `}<strong parentName="p">{`comma (,)`}</strong>{`.
Another possibility is adding the `}<strong parentName="p">{`"break"`}</strong>{` tag with an attribute `}<strong parentName="p">{`"value"`}</strong>{` in seconds (s) or milliseconds (ms).`}</p>
        <p>{`It is also possible to change the `}<inlineCode parentName="p">{`speaking rate`}</inlineCode>{`, you can use the `}<strong parentName="p">{`"prosody"`}</strong>{` tag with an attribute "value" in percentage.`}</p>
      </LeftColumn>
      <RightColumn mdxType="RightColumn">
  <h4>Delay with a comma</h4>
  <CodeSnippet code={CODE.TTS_BODY1} mdxType="CodeSnippet" />
        <h4>Delay with a break tag</h4>
        <CodeSnippet code={CODE.TTS_BODY2} mdxType="CodeSnippet" />
        <h4>Speaking rate</h4>
  <CodeSnippet code={CODE.TTS_BODY3} mdxType="CodeSnippet" />
      </RightColumn>
    </APIWrapper>
    <hr></hr>
    <APIWrapper mdxType="APIWrapper">
      <LeftColumn mdxType="LeftColumn">
        <h2>{`Send a voice message`}</h2>
        <p>{`This request creates a new voice message object. MessageBird returns the created voice message object with each request. Per request, a max of 50 recipients can be entered.`}</p>
      </LeftColumn>
      <RightColumn mdxType="RightColumn">
  <h4>Example request</h4>
  <CodeSnippet code={CODE.SENDTTS_REQUEST} mdxType="CodeSnippet" />
      </RightColumn>
    </APIWrapper>
    <h4>{`Required parameters`}</h4>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Parameter`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`body`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The body of the SMS message. `}<strong parentName="td">{`Required`}</strong></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`recipients`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`array`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The array of recipients msisdns. `}<strong parentName="td">{`Required`}</strong></td>
        </tr>
      </tbody>
    </table>
    <h4>{`Optional parameters`}</h4>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Parameter`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`originator`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The sender of the message. A telephone number (including country code).`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`reference`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A client reference`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`language`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The language in which the message needs to be read to the recipient. Possible values are: `}<inlineCode parentName="td">{`cy-gb`}</inlineCode>{`,`}<inlineCode parentName="td">{`da-dk`}</inlineCode>{`,`}<inlineCode parentName="td">{`de-de`}</inlineCode>{`,`}<inlineCode parentName="td">{`el-gr`}</inlineCode>{`,`}<inlineCode parentName="td">{`en-au`}</inlineCode>{`,`}<inlineCode parentName="td">{`en-gb`}</inlineCode>{`,`}<inlineCode parentName="td">{`en-gb-wls`}</inlineCode>{`,`}<inlineCode parentName="td">{`en-in`}</inlineCode>{`,`}<inlineCode parentName="td">{`en-us`}</inlineCode>{`,`}<inlineCode parentName="td">{`es-es`}</inlineCode>{`,`}<inlineCode parentName="td">{`es-mx`}</inlineCode>{`,`}<inlineCode parentName="td">{`es-us`}</inlineCode>{`,`}<inlineCode parentName="td">{`fr-ca`}</inlineCode>{`,`}<inlineCode parentName="td">{`fr-fr`}</inlineCode>{`,`}<inlineCode parentName="td">{`id-id`}</inlineCode>{`,`}<inlineCode parentName="td">{`is-is`}</inlineCode>{`,`}<inlineCode parentName="td">{`it-it`}</inlineCode>{`,`}<inlineCode parentName="td">{`ja-jp`}</inlineCode>{`,`}<inlineCode parentName="td">{`ko-kr`}</inlineCode>{`,`}<inlineCode parentName="td">{`ms-my`}</inlineCode>{`,`}<inlineCode parentName="td">{`nb-no`}</inlineCode>{`,`}<inlineCode parentName="td">{`nl-nl`}</inlineCode>{`,`}<inlineCode parentName="td">{`pl-pl`}</inlineCode>{`,`}<inlineCode parentName="td">{`pt-br`}</inlineCode>{`,`}<inlineCode parentName="td">{`pt-pt`}</inlineCode>{`,`}<inlineCode parentName="td">{`ro-ro`}</inlineCode>{`,`}<inlineCode parentName="td">{`ru-ru`}</inlineCode>{`,`}<inlineCode parentName="td">{`sv-se`}</inlineCode>{`,`}<inlineCode parentName="td">{`ta-in`}</inlineCode>{`,`}<inlineCode parentName="td">{`th-th`}</inlineCode>{`,`}<inlineCode parentName="td">{`tr-tr`}</inlineCode>{`,`}<inlineCode parentName="td">{`vi-vn`}</inlineCode>{`,`}<inlineCode parentName="td">{`zh-cn`}</inlineCode>{`,`}<inlineCode parentName="td">{`zh-hk`}</inlineCode>{`. `}<em parentName="td">{`Default: en-gb`}</em></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`voice`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The voice in which the messages needs to be read to the recipient. Possible values are: `}<inlineCode parentName="td">{`male`}</inlineCode>{`, `}<inlineCode parentName="td">{`female`}</inlineCode>{`. `}<em parentName="td">{`Default: female`}</em></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`repeat`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`int`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The number of times the message needs to be repeated. `}<em parentName="td">{`Default: 1`}</em></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`ifMachine`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`What to do when a machine picks up the phone? Possible values are:`}<br />{`- `}<inlineCode parentName="td">{`continue`}</inlineCode>{` do not check, just play the message`}<br />{`- `}<inlineCode parentName="td">{`delay`}</inlineCode>{` if a machine answers, wait until the machine stops talking`}<br />{`- `}<inlineCode parentName="td">{`hangup`}</inlineCode>{` hangup when a machine answers`}<br /><em parentName="td">{`Default is: `}<inlineCode parentName="em">{`delay`}</inlineCode></em>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`machineTimeout`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`integer`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The time (in milliseconds) to analyze if a machine has picked up the phone. Used in combination with the `}<inlineCode parentName="td">{`delay`}</inlineCode>{` and `}<inlineCode parentName="td">{`hangup`}</inlineCode>{` values of the `}<inlineCode parentName="td">{`ifMachine`}</inlineCode>{` attribute. Minimum: 400, maximum: 10000. `}<em parentName="td">{`Default: 7000`}</em></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`scheduledDatetime`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`datetime`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The scheduled date and time of the message in RFC3339 format (Y-m-d\\TH:i:sP)`}</td>
        </tr>
      </tbody>
    </table>
    <APIWrapper mdxType="APIWrapper">
      <LeftColumn mdxType="LeftColumn">
        <h4>{`Returns`}</h4>
        <p>{`Returns a message object if the request was successful. If the request failed, an `}<a parentName="p" {...{
            "href": "/api#api-errors"
          }}>{`error object`}</a>{` will be returned.`}</p>
      </LeftColumn>
      <RightColumn mdxType="RightColumn">
  <h4>Example response</h4>
  <CodeSnippet code={CODE.SENDTTS_RESPONSE} mdxType="CodeSnippet" />
      </RightColumn>
    </APIWrapper>
    <hr></hr>
    <APIWrapper mdxType="APIWrapper">
      <LeftColumn mdxType="LeftColumn">
        <h2>{`View a voice message`}</h2>
        <p>{`This request retrieves the information of an existing voice message. You only need to supply the unique voice message id that was returned upon creation.`}</p>
        <h4>{`Required parameters`}</h4>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Parameter`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Type`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Description`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`id`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`string`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`A unique random ID which is created on the MessageBird platform and is returned upon creation of the object. `}<strong parentName="td">{`Required`}</strong></td>
            </tr>
          </tbody>
        </table>
      </LeftColumn>
      <RightColumn mdxType="RightColumn">
  <h4>Definition</h4>
  <CodeSnippet code={CODE.VIEWTTS_DEF} mdxType="CodeSnippet" />
        <h4>Example request</h4>
  <CodeSnippet code={CODE.VIEWTTS_REQUEST} mdxType="CodeSnippet" />
      </RightColumn>
    </APIWrapper>
    <APIWrapper mdxType="APIWrapper">
      <LeftColumn mdxType="LeftColumn">
        <h4>{`Returns`}</h4>
        <p>{`If successful, this request will return a voice message object. If the request failed, an `}<a parentName="p" {...{
            "href": "/api#api-errors"
          }}>{`error object`}</a>{` will be returned.`}</p>
      </LeftColumn>
      <RightColumn mdxType="RightColumn">
  <h4>Example response</h4>
  <CodeSnippet code={CODE.VIEWTTS_RESPONSE} mdxType="CodeSnippet" />
      </RightColumn>
    </APIWrapper>
    <APIWrapper mdxType="APIWrapper">
      <LeftColumn mdxType="LeftColumn">
        <hr></hr>
        <h2>{`List voice messages`}</h2>
        <p>{`To list all inbound and outbound voice messages, you can do a GET request on the `}<inlineCode parentName="p">{`/messages`}</inlineCode>{` endpoint.`}</p>
        <p>{`Messages are identified by a unique random ID. You can use this ID to check the status of the SMS message through the provided endpoint.`}</p>
      </LeftColumn>
      <RightColumn mdxType="RightColumn">
  <h4>API Endpoint</h4>
  <CodeSnippet code={CODE.LISTTTS_REQUEST} mdxType="CodeSnippet" />
      </RightColumn>
    </APIWrapper>
    <h3>{`Filters`}</h3>
    <p>{`Besides listing all voice messages, the MessageBird Voice API also provides some filters that can be used as query parameters:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Filter`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`originator`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Display messages by the specified originator.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`recipient`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Display messages for the specified recipient.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`limit`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`int`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Limit the amount of messages listed.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`offset`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`int`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Skip first `}<inlineCode parentName="td">{`n`}</inlineCode>{` results.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`contact_id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`int`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Display messages by `}<inlineCode parentName="td">{`contact_id`}</inlineCode>{`. See `}<Link to="/docs/contacts" mdxType="Link">{`Contacts API`}</Link>{` for more info.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`status`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Display messages with status `}<inlineCode parentName="td">{`scheduled`}</inlineCode>{`, `}<inlineCode parentName="td">{`sent`}</inlineCode>{`, `}<inlineCode parentName="td">{`buffered`}</inlineCode>{`, `}<inlineCode parentName="td">{`delivered`}</inlineCode>{`, `}<inlineCode parentName="td">{`expired`}</inlineCode>{`, or `}<inlineCode parentName="td">{`delivery_failed`}</inlineCode>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`from`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`datetime`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Display messages starting from the specified date in RFC3339 format (Y-m-d\\TH:i:sP).`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`until`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`datetime`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Display messages until the specified date in RFC3339 format (Y-m-d\\TH:i:sP).`}</td>
        </tr>
      </tbody>
    </table>
    <APIWrapper mdxType="APIWrapper">
      <LeftColumn mdxType="LeftColumn">
        <h4>{`Returns`}</h4>
        <p>{`If successful, this request will return an object with a list of voice message objects. If the request failed, an `}<a parentName="p" {...{
            "href": "/api#api-errors"
          }}>{`error object`}</a>{` will be returned.`}</p>
      </LeftColumn>
      <RightColumn mdxType="RightColumn">
  <h4>Example response</h4>
  <CodeSnippet code={CODE.LISTTTS_RESPONSE} mdxType="CodeSnippet" />
      </RightColumn>
    </APIWrapper>
    <APIWrapper mdxType="APIWrapper">
      <LeftColumn mdxType="LeftColumn">
        <h2>{`Handle a status report`}</h2>
        <p>{`Status reports are requests that are sent to your platform through a GET request. The requests hold information about the status of a voice message that you have sent through our API. Status reports are only provided for voice messages that have a `}<inlineCode parentName="p">{`reference`}</inlineCode>{` and accounts that have configured their `}<a parentName="p" {...{
            "href": "https://dashboard.messagebird.com/developers/settings"
          }}>{`status report url`}</a>{`.`}</p>
        <h4>{`Attributes`}</h4>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Attribute`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Type`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Description`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`id`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`string`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`A unique random ID which is created on the MessageBird platform and is returned upon creation of the object.`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`reference`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`string`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`A client reference`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`recipient`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`string`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`The recipient where this status report applies to.`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`status`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`string`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`The status of the message sent to the recipient. Possible values: `}<inlineCode parentName="td">{`calling`}</inlineCode>{`, `}<inlineCode parentName="td">{`answered`}</inlineCode>{`, `}<inlineCode parentName="td">{`failed`}</inlineCode>{`. For future use the following status are reserved: `}<inlineCode parentName="td">{`busy`}</inlineCode>{` and `}<inlineCode parentName="td">{`machine`}</inlineCode></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`statusDatetime`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`datetime`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`The datum time of this status in RFC3339 format (Y-m-d\\TH:i:sP)`}</td>
            </tr>
          </tbody>
        </table>
      </LeftColumn>
      <RightColumn mdxType="RightColumn">
  <h4>Example request</h4>
  <CodeSnippet code={CODE.STATUSTTS_REQUEST} mdxType="CodeSnippet" />
      </RightColumn>
    </APIWrapper>
    <APIWrapper mdxType="APIWrapper">
      <LeftColumn mdxType="LeftColumn">
        <h3>{`Response`}</h3>
        <p>{`Your platform should respond with a `}<inlineCode parentName="p">{`200 OK`}</inlineCode>{` HTTP header. When our platform doesn't receive a `}<inlineCode parentName="p">{`200 OK`}</inlineCode>{` header, we will try to deliver the receipt again (up to 10 times).`}</p>
      </LeftColumn>
      <RightColumn mdxType="RightColumn">
  <h4>Example response</h4>
  <CodeSnippet code={CODE.STATUSTTS_RESPONSE} mdxType="CodeSnippet" />
      </RightColumn>
    </APIWrapper>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      